import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import axios from 'axios';
import { Calendar, Plus, Edit2, Trash2, Users, Clock, ChevronLeft, ChevronRight, Download, Printer } from 'lucide-react';import PageContainer from '../layout/PageContainer';




interface Staff {
  id: number;
  display_name: string;
  role_level: number;
  is_active: number;
}

interface Shift {
  id?: number;
  staff_id: number;
  staff_name?: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  break_time: number;
  shift_type: string;
  status: string;
  notes?: string;
}

interface ShiftTemplate {
  id: number;
  name: string;
  start_time: string;
  end_time: string;
  break_time: number;
  color: string;
  description?: string;
}

const ShiftManagement: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'weekly' | 'monthly'>('weekly');
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [templates, setTemplates] = useState<ShiftTemplate[]>([]);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [draggedShift, setDraggedShift] = useState<Shift | null>(null);

  useEffect(() => {
    fetchStaff();
    fetchTemplates();
    fetchShifts();
  }, [currentDate, viewMode]);

  const fetchStaff = async () => {
    try {
      const response = await apiClient.get('/staff/list');
      setStaff(response.data.filter((s: Staff) => s.is_active === 1));
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/shifts/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchShifts = async () => {
    setLoading(true);
    try {
      let response;
      if (viewMode === 'weekly') {
        const startDate = getWeekStart(currentDate);
        response = await apiClient.get('/shifts/weekly', {
          params: { startDate: formatDate(startDate) }
        });
      } else {
        response = await apiClient.get('/shifts/monthly', {
          params: { 
            year: currentDate.getFullYear(),
            month: currentDate.getMonth() + 1
          }
        });
      }
      setShifts(response.data);
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWeekStart = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const getWeekDays = () => {
    const weekStart = getWeekStart(currentDate);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  const getShiftForStaffAndDate = (staffId: number, date: string) => {
    return shifts.find(s => s.staff_id === staffId && s.shift_date === date);
  };

  const handleAddShift = (staffId: number, date: string) => {
    setSelectedShift({
      staff_id: staffId,
      shift_date: date,
      start_time: '09:00',
      end_time: '18:00',
      break_time: 60,
      shift_type: 'regular',
      status: 'scheduled'
    });
    setShowShiftModal(true);
  };

  const handleEditShift = (shift: Shift) => {
    setSelectedShift(shift);
    setShowShiftModal(true);
  };

  const handleDeleteShift = async (shiftId: number) => {
    if (!confirm('このシフトを削除しますか？')) return;
    
    try {
      await apiClient.delete('/shifts/${shiftId}');
      fetchShifts();
    } catch (error) {
      console.error('Failed to delete shift:', error);
      alert('シフトの削除に失敗しました');
    }
  };

  const handleSaveShift = async () => {
    if (!selectedShift) return;
    
    try {
      if (selectedShift.id) {
        await apiClient.put('/shifts/${selectedShift.id}', selectedShift);
      } else {
        await apiClient.post('/shifts', selectedShift);
      }
      setShowShiftModal(false);
      setSelectedShift(null);
      fetchShifts();
    } catch (error: any) {
      console.error('Failed to save shift:', error);
      alert(error.response?.data?.error || 'シフトの保存に失敗しました');
    }
  };

  const applyTemplate = (template: ShiftTemplate) => {
    if (!selectedShift) return;
    
    setSelectedShift({
      ...selectedShift,
      start_time: template.start_time,
      end_time: template.end_time,
      break_time: template.break_time,
      shift_type: template.name
    });
  };

  const handleDragStart = (shift: Shift) => {
    setDraggedShift(shift);
  };

  const handleDrop = async (staffId: number, date: string) => {
    if (!draggedShift) return;
    
    try {
      // 既存のシフトを削除
      if (draggedShift.id) {
        await apiClient.delete('/shifts/${draggedShift.id}');
      }
      
      // 新しいシフトを作成
      await apiClient.post('/shifts', {
        ...draggedShift,
        staff_id: staffId,
        shift_date: date
      });
      
      fetchShifts();
    } catch (error) {
      console.error('Failed to move shift:', error);
      alert('シフトの移動に失敗しました');
    } finally {
      setDraggedShift(null);
    }
  };

  const exportToCSV = () => {
    const headers = ['スタッフ名', '日付', '開始時間', '終了時間', '休憩時間', 'シフトタイプ'];
    const rows = shifts.map(s => [
      s.staff_name || '',
      s.shift_date,
      s.start_time,
      s.end_time,
      s.break_time.toString(),
      s.shift_type
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `shifts_${formatDate(currentDate)}.csv`;
    link.click();
  };

  const printShiftTable = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const days = viewMode === 'weekly' ? getWeekDays() : getMonthDays();
    
    const html = `
      <!DOCTYPE html>
      <html lang="ja">
      <head>
        <meta charset="UTF-8">
        <title>シフト表 - ${currentDate.getFullYear()}年${currentDate.getMonth() + 1}月</title>
        <style>
          body { font-family: 'MS Gothic', monospace; margin: 20px; }
          h1 { text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #000; padding: 8px; text-align: center; }
          th { background-color: #f0f0f0; font-weight: bold; }
          .shift-cell { font-size: 12px; }
          @media print {
            body { margin: 10mm; }
          }
        </style>
      </head>
      <body>
        <h1>シフト表 - ${currentDate.getFullYear()}年${currentDate.getMonth() + 1}月</h1>
        <table>
          <thead>
            <tr>
              <th>スタッフ</th>
              ${days.map(d => `<th>${d.getMonth() + 1}/${d.getDate()}(${['日','月','火','水','木','金','土'][d.getDay()]})</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${staff.map(s => `
              <tr>
                <td>${s.display_name}</td>
                ${days.map(d => {
                  const shift = getShiftForStaffAndDate(s.id, formatDate(d));
                  return `<td class="shift-cell">${shift ? `${formatTime(shift.start_time)}-${formatTime(shift.end_time)}` : '-'}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 100);
  };

  const navigate = (direction: number) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'weekly') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const days = viewMode === 'weekly' ? getWeekDays() : getMonthDays();
  const dayNames = ['日', '月', '火', '水', '木', '金', '土'];

  return (
    <PageContainer>{/* ヘッダー */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-8 w-8 text-blue-600" />
            シフト管理
          </h1>
          
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('weekly')}
              className={`px-4 py-2 rounded-lg ${viewMode === 'weekly' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              週表示
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`px-4 py-2 rounded-lg ${viewMode === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              月表示
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {viewMode === 'weekly' 
                ? `${formatDate(getWeekStart(currentDate))} 〜 ${formatDate(new Date(getWeekStart(currentDate).getTime() + 6 * 24 * 60 * 60 * 1000))}`
                : `${currentDate.getFullYear()}年${currentDate.getMonth() + 1}月`
              }
            </h2>
            <button onClick={() => navigate(1)} className="p-2 hover:bg-gray-100 rounded">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="h-4 w-4" />
              CSV出力
            </button>
            <button
              onClick={printShiftTable}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Printer className="h-4 w-4" />
              印刷
            </button>
          </div>
        </div>
      </div>

      {/* シフト表 */}
      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 bg-gray-50 px-4 py-3 text-left font-semibold">スタッフ</th>
              {days.map(day => (
                <th key={formatDate(day)} className="px-2 py-3 text-center min-w-[100px]">
                  <div className="text-sm font-semibold">{day.getMonth() + 1}/{day.getDate()}</div>
                  <div className={`text-xs ${day.getDay() === 0 ? 'text-red-500' : day.getDay() === 6 ? 'text-blue-500' : 'text-gray-600'}`}>
                    ({dayNames[day.getDay()]})
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {staff.map(s => (
              <tr key={s.id} className="border-t hover:bg-gray-50">
                <td className="sticky left-0 bg-white px-4 py-3 font-medium">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-400" />
                    {s.display_name}
                  </div>
                </td>
                {days.map(day => {
                  const dateStr = formatDate(day);
                  const shift = getShiftForStaffAndDate(s.id, dateStr);
                  
                  return (
                    <td
                      key={dateStr}
                      className="px-2 py-2 text-center border-l relative group"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleDrop(s.id, dateStr)}
                    >
                      {shift ? (
                        <div
                          className="bg-blue-100 rounded px-2 py-1 text-xs cursor-move hover:bg-blue-200"
                          draggable
                          onDragStart={() => handleDragStart(shift)}
                        >
                          <div className="font-semibold">
                            {formatTime(shift.start_time)}-{formatTime(shift.end_time)}
                          </div>
                          <div className="text-gray-600">
                            休憩{shift.break_time}分
                          </div>
                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 flex gap-1">
                            <button
                              onClick={() => handleEditShift(shift)}
                              className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              <Edit2 className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteShift(shift.id!)}
                              className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAddShift(s.id, dateStr)}
                          className="w-full h-full min-h-[60px] hover:bg-gray-100 flex items-center justify-center opacity-0 hover:opacity-100"
                        >
                          <Plus className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* シフト編集モーダル */}
      {showShiftModal && selectedShift && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">
              {selectedShift.id ? 'シフト編集' : 'シフト追加'}
            </h3>

            {/* テンプレート選択 */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">テンプレート</label>
              <div className="grid grid-cols-2 gap-2">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className="p-2 border rounded text-sm hover:bg-gray-50"
                    style={{ borderColor: t.color }}
                  >
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-gray-600">
                      {formatTime(t.start_time)}-{formatTime(t.end_time)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">開始時間</label>
              <input
                type="time"
                value={selectedShift.start_time}
                onChange={(e) => setSelectedShift({...selectedShift, start_time: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">終了時間</label>
              <input
                type="time"
                value={selectedShift.end_time}
                onChange={(e) => setSelectedShift({...selectedShift, end_time: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">休憩時間（分）</label>
              <input
                type="number"
                value={selectedShift.break_time}
                onChange={(e) => setSelectedShift({...selectedShift, break_time: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">メモ</label>
              <textarea
                value={selectedShift.notes || ''}
                onChange={(e) => setSelectedShift({...selectedShift, notes: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowShiftModal(false);
                  setSelectedShift(null);
                }}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveShift}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default ShiftManagement;